import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { WelcomeScreen } from "@/components/WelcomeScreen";
import { MapView } from "@/components/MapView";
import { ChatGuide } from "@/components/ChatGuide";
import { MonumentSheet } from "@/components/MonumentSheet";
import { BottomNav } from "@/components/BottomNav";
import { PaymentGate } from "@/components/PaymentGate";
import { PodcastList } from "@/components/PodcastList";
import { SettingsView } from "@/components/SettingsView";
import { MicButton } from "@/components/MicButton";
import { fetchMonuments, fetchWelcome, checkPaymentStatus } from "@/lib/api";
import { usePremium } from "@/hooks/usePremium";
import { useSpeech } from "@/hooks/useSpeech";
import { PAYMENTS_ENABLED, HERO_IMG } from "@/config";

// Los vídeos son ficheros estáticos servidos por el propio frontend
// (guia.lermahoy.es/videos/...). No dependemos del campo video_url que
// devuelve el backend, porque puede quedarse desactualizado en la base
// de datos y desincronizarse de los ficheros reales.
const VIDEO_BASE = "https://guia.lermahoy.es/videos";
const STATIC_VIDEO_FILES = {
  "m-palacio": "Palacio Ducal.mp4",
  "m-colegiata": "Ex-Colegita de San Pedro.mp4",
  "m-plaza-mayor": "Plaza Mayor.mp4",
  "m-pasadizo": "Pasadizo elevado.mp4",
  "m-santa-clara": "Plaza de Santa Clara.mp4",
  "m-ascension": "Monasterio de Santa Clara.mp4",
  "m-santa-teresa": "Convento de Santa Teresa.mp4",
  "m-san-blas": "Monasterio de San Blas.mp4",
  "m-madre-dios": "1-Monasterio del Carmen.mp4",
  "m-santo-domingo": "Convento de Santo Domingo.mp4",
  "m-san-francisco": "Convento San Francisco de lo Reyes.mp4",
  "m-puente": "Puente Medieval.mp4",
  "m-arco-carcel": "Arco de la carcel.mp4",
};
function withStaticVideoUrls(monuments) {
  return monuments.map((m) => {
    const filename = STATIC_VIDEO_FILES[m.id];
    if (!filename) return m;
    return { ...m, video_url: `${VIDEO_BASE}/${encodeURIComponent(filename)}` };
  });
}

function sessionId() {
  let s = localStorage.getItem("lerma_session");
  if (!s) {
    // Este codigo es lo unico que protege el historial de chat de cada
    // visitante (no hay usuarios ni contraseñas en esta app), asi que usamos
    // el generador de numeros aleatorios seguro del navegador en vez de
    // Math.random(), que es predecible y no vale para esto.
    if (window.crypto && window.crypto.randomUUID) {
      s = "s-" + window.crypto.randomUUID();
    } else {
      s = "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
    localStorage.setItem("lerma_session", s);
  }
  return s;
}

function App() {
    const [entered, setEntered] = useState(false);
   const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(t);
  }, []);

  // Firefox exige que speechSynthesis.speak() se dispare de forma SÍNCRONA
  // dentro de un gesto real del usuario (clic/toque/tecla). Nuestras voces
  // de Catalina se lanzan casi siempre después de esperar una respuesta del
  // servidor (async), así que en Firefox se descartan en silencio sin dar
  // ningún error. Truco estándar: en el primer toque/clic/tecla de toda la
  // página, lanzamos una frase vacía de forma síncrona para "desbloquear"
  // la síntesis de voz para el resto de la sesión. Chrome no necesita esto,
  // pero no le hace ningún daño.
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const unlockSpeech = () => {
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(""));
    };
    window.addEventListener("pointerdown", unlockSpeech, { once: true });
    window.addEventListener("keydown", unlockSpeech, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlockSpeech);
      window.removeEventListener("keydown", unlockSpeech);
    };
  }, []);
  const [center, setCenter] = useState({ lat: 42.0266, lng: -3.7576 });
  const [monuments, setMonuments] = useState([]);
  const [welcomeText, setWelcomeText] = useState("");
  const [view, setView] = useState("map");
  const [selected, setSelected] = useState(null);
  const [chatMonument, setChatMonument] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState(null);

  const { premium, activate, expiresAt } = usePremium();
  const { speak, stop, speaking } = useSpeech();
  const sid = sessionId();
    const FREE_MONUMENT_ID = "m-plaza-mayor";
  const unlocked = PAYMENTS_ENABLED ? premium || selected?.id === FREE_MONUMENT_ID : true;

  const loadMonuments = useCallback(async () => {
    const data = await fetchMonuments();
    setCenter(data.center);
    setMonuments(withStaticVideoUrls(data.monuments));
  }, []);

  useEffect(() => {
    loadMonuments();
    fetchWelcome().then(setWelcomeText).catch(() => {});
  }, [loadMonuments]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const stripeSession = params.get("session_id");
    if (payment === "success" && stripeSession) {
      setEntered(true);
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const st = await checkPaymentStatus(stripeSession);
          if (st.payment_status === "paid") {
            activate();
            toast.success("¡Pago completado! Ya tienes acceso a toda la audioguía.");
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }
        } catch (e) {}
        if (tries < 8) setTimeout(poll, 1500);
        else window.history.replaceState({}, document.title, window.location.pathname);
      };
      poll();
    } else if (payment === "cancel") {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBuy = () => setPayOpen(true);

  const enterApp = () => {
    setEntered(true);
    if (welcomeText) setTimeout(() => speak(welcomeText), 400);
  };

  const askCatalina = (mon) => {
    setSelected(null);
    setChatMonument(mon);
    setView("chat");
  };

  const onVoice = (text) => {
    setChatMonument(selected || chatMonument);
    setPendingQuestion(text);
    setView("chat");
  };

   if (showSplash) {
    return (
      <div className="App" style={{ background: "#0b0d12" }}>
        <img
          src={HERO_IMG}
          alt="Catalina"
          style={{ width: "100%", height: "100%", objectFit: "cover", position: "fixed", inset: 0 }}
        />
      </div>
    );
  }

  if (!entered) {
    return (
      <div className="App">
        <Toaster position="top-center" theme="dark" richColors />
        <WelcomeScreen onEnter={enterApp} welcomeText={welcomeText} onBuy={openBuy} />
      </div>
    );
  }

  return (
    <div className="App grain-overlay" style={{ background: "#0b0d12" }}>
      <Toaster position="top-center" theme="dark" richColors />

      {view === "map" && (
        <MapView
          center={center}
          monuments={monuments}
          onSelect={setSelected}
          activeId={selected?.id}
          speak={speak}
          onApproach={(m) =>
            toast(`Catalina: cerca de ${m.title}`, { description: "Toca para escuchar su historia." })
          }
        />
      )}
      {view === "chat" && (
        <ChatGuide
          sessionId={sid}
          monument={chatMonument}
          speak={speak}
          stopSpeak={stop}
          speaking={speaking}
          autoTts
          pendingMessage={pendingQuestion}
          onConsumePending={() => setPendingQuestion(null)}
                    onTranscript={onVoice}
        />
      )}
      {view === "podcasts" && (
        <PodcastList monuments={monuments} premium={unlocked} onSelect={setSelected} onBuy={openBuy} />
      )}
      {view === "settings" && (
        <SettingsView
          premium={premium}
          expiresAt={expiresAt}
          monuments={monuments}
          onBuy={openBuy}
          onReload={loadMonuments}
          paymentsEnabled={PAYMENTS_ENABLED}
        />
      )}

      <MonumentSheet
        monument={selected}
        premium={unlocked}
        sessionId={sid}
        speaking={speaking}
        onSpeak={speak}
        onStopSpeak={stop}
        onAskCatalina={askCatalina}
        onBuy={openBuy}
        onClose={() => {
          stop();
          setSelected(null);
        }}
      />

      {PAYMENTS_ENABLED && <PaymentGate open={payOpen} sessionId={sid} onClose={() => setPayOpen(false)} />}

     
      <BottomNav view={view} setView={setView} />
    </div>
  );
}

export default App;
